import { useEffect, useRef, forwardRef } from "react";

/**
 * ButterflyCursor — a small family of 🦋 butterflies follows the pointer.
 * Each butterfly leaves a single thin glowing line trail behind it that
 * gently fades. The line colour adapts to background brightness.
 */
const NUM_BUTTERFLIES = 5;
const TRAIL_LIFE = 90;        // frames each segment lives
const MIN_SEG_DIST = 4;       // skip new segment if too close to previous

const PALETTES = {
  light: {
    body: "#1A2E24",
    line: "rgba(212, 175, 55, 0.85)",  // gold
    lineEdge: "rgba(212, 175, 55, 0)",
  },
  dark: {
    body: "#FDFBF7",
    line: "rgba(255, 231, 168, 0.95)", // warm gold cream
    lineEdge: "rgba(255, 231, 168, 0)",
  },
};

export default function ButterflyCursor() {
  const wrapRef = useRef(null);
  const butterflyRefs = useRef([]);
  const stateRef = useRef([]);
  const targetRef = useRef({ x: 0, y: 0 });
  const trailCanvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    document.body.classList.add("has-butterfly-cursor");
    targetRef.current.x = window.innerWidth / 2;
    targetRef.current.y = window.innerHeight / 2;

    // Each butterfly keeps its own trail history (array of {x,y,life,paletteName})
    stateRef.current = Array.from({ length: NUM_BUTTERFLIES }, (_, i) => ({
      x: window.innerWidth / 2 + (Math.random() - 0.5) * 150,
      y: window.innerHeight / 2 + (Math.random() - 0.5) * 150,
      vx: (Math.random() - 0.5) * 1.5,
      vy: (Math.random() - 0.5) * 1.5,
      scale: i === 0 ? 1.0 : 0.55 + Math.random() * 0.3,
      offsetAngle: (i / NUM_BUTTERFLIES) * Math.PI * 2,
      offsetR: i === 0 ? 0 : 35 + Math.random() * 50,
      paletteName: "light",
      trail: [],
    }));

    const canvas = trailCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const sizeCanvas = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sizeCanvas();

    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
    };
    const onResize = () => sizeCanvas();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", onResize);

    // Sample background under the cursor (slow loop — 6fps)
    const updatePaletteFromBg = () => {
      const el = document.elementFromPoint(
        targetRef.current.x,
        targetRef.current.y
      );
      if (!el) return;
      let cur = el;
      let bg = "rgba(0,0,0,0)";
      while (cur && cur !== document.body) {
        const cbg = getComputedStyle(cur).backgroundColor;
        if (cbg && cbg !== "rgba(0, 0, 0, 0)" && cbg !== "transparent") {
          bg = cbg;
          break;
        }
        cur = cur.parentElement;
      }
      if (bg === "rgba(0,0,0,0)") {
        bg = getComputedStyle(document.body).backgroundColor;
      }
      const m = bg.match(/rgba?\(([^)]+)\)/);
      let isDark = false;
      if (m) {
        const [r, g, b] = m[1].split(",").map((s) => parseFloat(s.trim()));
        isDark = 0.299 * r + 0.587 * g + 0.114 * b < 130;
      }
      const target = isDark ? "dark" : "light";
      stateRef.current.forEach((b) => (b.paletteName = target));
    };
    const paletteInterval = setInterval(updatePaletteFromBg, 160);

    let raf;
    const tick = () => {
      const butterflies = stateRef.current;
      const tx = targetRef.current.x;
      const ty = targetRef.current.y;

      // Clear canvas every frame; the lines are redrawn from history
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      butterflies.forEach((b, i) => {
        // Halo target around the cursor
        const tgtX =
          tx +
          Math.cos(b.offsetAngle + performance.now() * 0.0006) * b.offsetR;
        const tgtY =
          ty +
          Math.sin(b.offsetAngle + performance.now() * 0.0006) * b.offsetR;
        b.vx += (tgtX - b.x) * 0.018;
        b.vy += (tgtY - b.y) * 0.018;
        const wander = b.scale < 0.8 ? 0.45 : 0.2;
        b.vx += (Math.random() - 0.5) * wander;
        b.vy += (Math.random() - 0.5) * wander;
        b.vx *= 0.92;
        b.vy *= 0.92;
        const sp = Math.hypot(b.vx, b.vy);
        const maxSp = 6;
        if (sp > maxSp) {
          b.vx = (b.vx / sp) * maxSp;
          b.vy = (b.vy / sp) * maxSp;
        }
        b.x += b.vx;
        b.y += b.vy;

        // Append to trail if moved enough
        const last = b.trail[b.trail.length - 1];
        if (!last || Math.hypot(last.x - b.x, last.y - b.y) > MIN_SEG_DIST) {
          b.trail.push({ x: b.x, y: b.y, life: 0, palette: b.paletteName });
        }
        // Age & prune
        b.trail.forEach((s) => s.life++);
        while (b.trail.length && b.trail[0].life > TRAIL_LIFE) b.trail.shift();

        // Draw single thin line through this butterfly's trail
        if (b.trail.length >= 2) {
          for (let j = 1; j < b.trail.length; j++) {
            const p0 = b.trail[j - 1];
            const p1 = b.trail[j];
            const t = 1 - p1.life / TRAIL_LIFE;
            const pal = PALETTES[p1.palette] || PALETTES.light;
            ctx.strokeStyle = pal.line.replace(/[\d.]+\)$/, `${(t * 0.85).toFixed(3)})`);
            ctx.lineWidth = 1.1; // thin
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(p0.x, p0.y);
            ctx.lineTo(p1.x, p1.y);
            ctx.stroke();
          }
        }

        // Apply transform to butterfly element
        const el = butterflyRefs.current[i];
        if (el) {
          // Emoji has its own orientation — keep it upright (no rotation) but
          // tilt slightly toward direction of motion for a flying feel.
          const tilt = Math.max(-25, Math.min(25, b.vx * 4));
          el.style.transform = `translate3d(${b.x - 12}px, ${b.y - 12}px, 0) rotate(${tilt.toFixed(1)}deg) scale(${b.scale.toFixed(2)})`;
          const pal = PALETTES[b.paletteName] || PALETTES.light;
          el.style.filter =
            pal === PALETTES.dark
              ? "drop-shadow(0 0 6px rgba(255,231,168,0.7))"
              : "drop-shadow(0 1px 2px rgba(0,0,0,0.25))";
        }
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(paletteInterval);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      document.body.classList.remove("has-butterfly-cursor");
    };
  }, []);

  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(pointer: coarse)").matches
  ) {
    return null;
  }

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 9999 }}
    >
      {/* Thin glowing trail */}
      <canvas
        ref={trailCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
      {/* Butterfly emojis */}
      {Array.from({ length: NUM_BUTTERFLIES }, (_, i) => (
        <ButterflyEmoji key={i} ref={(el) => (butterflyRefs.current[i] = el)} />
      ))}
    </div>
  );
}

const ButterflyEmoji = forwardRef(function ButterflyEmoji(_props, ref) {
  return (
    <span
      ref={ref}
      className="fixed top-0 left-0 will-change-transform select-none"
      style={{
        fontSize: "24px",
        lineHeight: 1,
        // Browser-rendered emoji
        fontFamily:
          "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif",
      }}
    >
      🦋
    </span>
  );
});
